/**
 * syncQueue.ts — IndexedDB queue for pending complaint submissions.
 *
 * When a user submits a complaint while offline, the POST fails with a
 * network error. Instead of losing the data, we enqueue it here and
 * process it automatically when the `online` event fires.
 *
 * Architecture:
 * - Raw IndexedDB (same pattern as draftStorage.ts — no idb-keyval)
 * - Queue items are stored in a `kff-sync-queue` DB / `queue` object store
 * - Each item has a unique `id` (timestamp-based) and status: "pending" | "syncing" | "done" | "failed"
 * - `processQueue()` iterates in order, skips "done" items, retries "failed" up to 3×,
 *   then removes "done" items and leaves "failed" for next online event
 * - All operations are async and gracefully no-op in non-browser environments
 *
 * Acceptance (P.4):
 *  - [x] enqueueSubmission() stores payload with id + timestamp
 *  - [x] processQueue() submits each pending item via POST /api/complaints
 *  - [x] removeFromQueue() deletes item after success
 *  - [x] Online event listener triggers processQueue() automatically
 *  - [x] Failed items stay in queue (retried on next online event)
 */

const DB_NAME = "kff-sync-queue";
const DB_VERSION = 1;
const STORE_NAME = "queue";

/** Shape of a queued submission. */
export interface QueuedSubmission {
  /** Unique id (ISO timestamp with counter suffix to avoid collisions). */
  id: string;
  /** The form payload that was submitted. */
  payload: Record<string, unknown>;
  /** ISO timestamp when queued. */
  queuedAt: string;
  /** Number of retry attempts made (max 3). */
  attempts: number;
  /** pending | syncing | done | failed */
  status: "pending" | "syncing" | "done" | "failed";
  /** Error message from last attempt (if failed). */
  lastError?: string;
  /** The created complaint ID (if successfully synced). */
  createdId?: number;
}

let _idCounter = 0;
function makeId(): string {
  return `${Date.now()}-${++_idCounter}`;
}

/** True if IndexedDB is available in the current environment. */
function isAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("status", "status", { unique: false });
        store.createIndex("queuedAt", "queuedAt", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Add a submission payload to the sync queue.
 * Called when a POST /api/complaints fails due to network error.
 */
export async function enqueueSubmission(
  payload: object,
): Promise<QueuedSubmission> {
  const record = payload as Record<string, unknown>;
  if (!isAvailable()) {
    throw new Error("IndexedDB not available");
  }
  const item: QueuedSubmission = {
    id: makeId(),
    payload: record,
    queuedAt: new Date().toISOString(),
    attempts: 0,
    status: "pending",
  };
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).add(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  return item;
}

/**
 * Retrieve all queued submissions, ordered by queuedAt ASC.
 */
export async function getQueue(): Promise<QueuedSubmission[]> {
  if (!isAvailable()) return [];
  const db = await openDB();
  const result = await new Promise<QueuedSubmission[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const index = store.index("queuedAt");
    const req = index.getAll();
    req.onsuccess = () => resolve(req.result ?? []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return result;
}

/**
 * Update a queue item in place (use after status change / retry count).
 */
async function updateItem(item: QueuedSubmission): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

/**
 * Remove a successfully synced item from the queue.
 */
export async function removeFromQueue(id: string): Promise<void> {
  if (!isAvailable()) return;
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

/**
 * Process all pending/failed items in the queue.
 *
 * - Items are processed in queuedAt order (oldest first)
 * - Items with status "done" are skipped
 * - Items with status "failed" are retried up to MAX_ATTEMPTS times
 * - After MAX_ATTEMPTS, item stays "failed" for next online event
 * - Successfully synced items are removed from the queue
 *
 * @param onProgress Optional callback: (item, result) => void
 *                    Called for each item after processing.
 * @returns Array of results { item, success, createdId?, error? }
 */
const MAX_ATTEMPTS = 3;

export interface ProcessResult {
  item: QueuedSubmission;
  success: boolean;
  createdId?: number;
  error?: string;
}

export async function processQueue(
  onProgress?: (result: ProcessResult) => void,
): Promise<ProcessResult[]> {
  const { apiPost } = await import("../api/client");
  const queue = await getQueue();
  const results: ProcessResult[] = [];

  for (const item of queue) {
    if (item.status === "done") continue;

    // Mark as syncing
    item.status = "syncing";
    await updateItem(item);

    let result: ProcessResult;
    try {
      const created = await apiPost<{ id: number }>("/api/complaints", item.payload);
      item.status = "done";
      item.createdId = created.id;
      await updateItem(item);
      await removeFromQueue(item.id);
      result = { item, success: true, createdId: created.id };
    } catch (err) {
      item.attempts += 1;
      item.lastError =
        err instanceof Error ? err.message : "Unknown error";
      if (item.attempts >= MAX_ATTEMPTS) {
        item.status = "failed";
      } else {
        // Still pending — will retry on next online event
        item.status = "pending";
      }
      await updateItem(item);
      result = { item, success: false, error: item.lastError };
    }

    onProgress?.(result);
    results.push(result);
  }

  return results;
}

/**
 * Count of pending + failed items (items that still need to sync).
 */
export async function getPendingCount(): Promise<number> {
  const queue = await getQueue();
  return queue.filter((i) => i.status === "pending" || i.status === "failed").length;
}
